/* eslint-disable @typescript-eslint/no-explicit-any */
import { FileLoader } from "./fileloader";
import { GLTFBinaryData } from "./glb-decoder";
import { GlTf, GlTfId } from "./gltf";

/** Spec: https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#accessor-element-size */
export const GLTF_COMPONENT_TYPE_ARRAYS: Record<
    number,
    | typeof Int8Array
    | typeof Uint8Array
    | typeof Int16Array
    | typeof Uint16Array
    | typeof Uint32Array
    | typeof Float32Array
> = {
    5120: Int8Array,
    5121: Uint8Array,
    5122: Int16Array,
    5123: Uint16Array,
    5125: Uint32Array,
    5126: Float32Array,
};

/** Spec: https://github.com/KhronosGroup/glTF/tree/master/specification/2.0#accessor-element-size */
export const GLTF_ELEMENTS_PER_TYPE: { [index: string]: number } = {
    SCALAR: 1,
    VEC2: 2,
    VEC3: 3,
    VEC4: 4,
    MAT2: 4,
    MAT3: 9,
    MAT4: 16,
};

// TODO!!: function required in this form?
export function resolveURL(url: string, path: string) {
    // Invalid URL
    if (typeof url !== "string" || url === "") {
        return "";
    }
    // Absolute URL http://,https://,//
    if (/^(https?:)?\/\//i.test(url)) {
        return url;
    }
    // Data URI
    if (/^data:.*,.*$/i.test(url)) {
        return url;
    }
    // Blob URL
    if (/^blob:.*$/i.test(url)) {
        return url;
    }
    // Relative URL
    return path + url;
}

// tslint:disable:max-classes-per-file
export class BufferData {
    asset: GltfAsset;
    baseUri: string;

    private bufferCache: ArrayBuffer[] = [];

    constructor(asset: GltfAsset, baseUri: string) {
        this.asset = asset;
        this.baseUri = baseUri;
    }

    async getBuffer(index: GlTfId): Promise<ArrayBuffer> {
        if (this.bufferCache[index] !== undefined) {
            return this.bufferCache[index];
        }

        const gltf = this.asset.gltf;
        if (!gltf.buffers) {
            /* istanbul ignore next */
            throw new Error("No buffers found.");
        }
        const buffer = gltf.buffers[index];
        // If present, GLB container is required to be the first buffer.
        if (buffer.uri === undefined) {
            /* istanbul ignore next */
            if (index !== 0) {
                throw new Error("GLB container is required to be the first buffer");
            }
            if (this.asset.glbData === undefined) {
                throw new Error("invalid gltf: buffer has no uri nor is there a GLB buffer");
            }
            return this.asset.glbData.binaryChunk;
        }

        const url = resolveURL(buffer.uri + `.gz`, this.baseUri);
        //console.log(buffer.uri);
        const bufferData: ArrayBuffer = await FileLoader.load(url);
        this.bufferCache[index] = bufferData;
        return bufferData;
    }

    /** Pre-fetches all buffer data. */
    async preFetchAll(): Promise<void[]> {
        const buffers = this.asset.gltf.buffers;
        if (!buffers) {
            return [];
        }
        return Promise.all(buffers.map((_, i): any => this.getBuffer(i))) as Promise<void[]>;
    }
}

export class ImageData {
    asset: GltfAsset;
    baseUri: string;
    /** crossorigin value for file and image requests */
    crossOrigin = "anonymous";

    private imageCache: Array<HTMLImageElement> = [];

    constructor(asset: GltfAsset, baseUri: string) {
        this.asset = asset;
        this.baseUri = baseUri;
    }

    /**
     * Get the image data. Triggers a network request if image is in an external file
     * and is accessed for the first time (cached afterwards). To avoid any delays,
     * use `preFetchAll` to pre-fetch everything.
     */
    /*  async get(index: GlTfId): Promise<HTMLImageElement> {
        if (this.imageCache[index] !== undefined) {
            return this.imageCache[index];
        }

        const gltf = this.asset.gltf;
        if (!gltf.images) {
            throw new Error("No images found.");
        }
        const image = gltf.images[index];

        let sourceURI: string;
        let isObjectURL = false;
        if (image.bufferView !== undefined) {
            // Load binary image data from bufferView, if provided.
            const bufferView = await this.asset.bufferViewData(image.bufferView);
            isObjectURL = true;
            const blob = new Blob([bufferView], { type: image.mimeType });
            sourceURI = URL.createObjectURL(blob);
        } else if (image.uri !== undefined) {
            sourceURI = resolveURL(image.uri, this.baseUri);
        } else {
            throw new Error("Invalid glTF: image must either have a `uri` or a `bufferView`");
        }

        const img = new Image();
        img.crossOrigin = this.crossOrigin;

        const promise: Promise<HTMLImageElement> = new Promise((resolve, reject) => {
            img.onerror = () => {
                reject(`Failed to load ${sourceURI}`);
            };
            img.onload = () => {
                if (isObjectURL) {
                    URL.revokeObjectURL(sourceURI);
                }
                this.imageCache[index] = img;
                resolve(img);
            };
            // TODO!: onprogress?
            img.src = sourceURI;
        });

        return promise;
    } */

    /* async preFetchAll(): Promise<void[]> {
        const images = this.asset.gltf.images;
        if (!images) {
            return [];
        }
        return Promise.all(images.map((_, i): any => this.get(i))) as Promise<void[]>;
    } */
}

export class GltfAsset {
    /** The JSON part of the asset. */
    gltf: GlTf;
    glbData: GLTFBinaryData | undefined;
    /** Helper for accessing buffer data */
    bufferData: BufferData;
    /** Helper for accessing image data */
    imageData: ImageData;

    constructor(gltf: GlTf, baseUri: string, glbData: GLTFBinaryData | undefined) {
        this.gltf = gltf;
        this.glbData = glbData;
        this.bufferData = new BufferData(this, baseUri);
        this.imageData = new ImageData(this, baseUri);
    }

    async LoadBuffer(index: number): Promise<ArrayBuffer> {
        if (this.gltf.buffers) {
            const curBuffer = this.gltf.buffers[index];

            if (curBuffer.type && curBuffer.type !== "arraybuffer") {
                console.error("BUFFER BAD TYPE");
            }

            const bufferData = this.bufferData.getBuffer(index);

            return bufferData;
        } else {
            console.error("NO BUFFER");
            throw new Error("No buffer views found.");
        }
    }

    async LoadBufferView(index: GlTfId) {
        if (this.gltf.bufferViews) {
            const curBufView = this.gltf.bufferViews[index];

            const byteLength = curBufView.byteLength || 0;
            const byteOffset = curBufView.byteOffset || 0;

            const arrBuffer = await this.LoadBuffer(curBufView.buffer);
            return arrBuffer.slice(byteOffset, byteOffset + byteLength);
        } else {
            console.error("NO BUFFER VIEW");
            throw new Error("No buffer views found.");
        }
    }

    async LoadAccessor(index: GlTfId) {
        if (!this.gltf.accessors) {
            console.error(`NO ACCESSORS`, this.gltf);
            return new Uint8Array(0);
        }

        const acc = this.gltf.accessors[index];

        let array;

        if (acc.bufferView == undefined || this.gltf.bufferViews == undefined) {
            console.error(`MISSING BUFFER VIEW`, acc);
            return new Uint8Array(0);
        }

        if (acc.bufferView !== undefined) {
            //load buffer view
            const bufferView = await this.LoadBufferView(acc.bufferView);

            const itemSize = GLTF_ELEMENTS_PER_TYPE[acc.type];
            const TypedArray = GLTF_COMPONENT_TYPE_ARRAYS[acc.componentType];

            // For VEC3: itemSize is 3, elementBytes is 4, itemBytes is 12.
            const elementBytes = TypedArray.BYTES_PER_ELEMENT;
            const itemBytes = elementBytes * itemSize;
            const byteOffset = acc.byteOffset || 0;
            const byteStride =
                acc.bufferView !== undefined ? this.gltf.bufferViews[acc.bufferView].byteStride : undefined;
            //const normalized = acc.normalized === true;

            // The buffer is not interleaved if the stride is the item size in bytes.
            if (byteStride && byteStride !== itemBytes) {
                console.error(`INTERLEAVED BUFFER`, acc);
                // Each "slice" of the buffer, as defined by 'count' elements of 'byteStride' bytes, gets its own InterleavedBuffer
                // This makes sure that IBA.count reflects accessor.count properly
            } else {
                if (bufferView === null) {
                    console.error(`BUFFER VIEW ERROR`, acc);
                } else {
                    array = new TypedArray(bufferView, byteOffset, acc.count * itemSize);
                }
            }
        }

        if (array === undefined) {
            console.error(`ERROR CREATING ACCESSOR`, acc);
            return new Uint8Array();
        } else {
            return array;
        }
    }
}
